// src/hooks/useAppPermissions.ts

import { useMemo } from 'react';
import { usePermissions as useRaPermissions } from 'react-admin';
import { 
  BasePermissions, 
  ResourceName, 
  ResourceAction,
  UserRole,
  ROLE_LEVELS,
  PermissionContext,
} from '@/config/permissions/types';
import { 
  PERMISSIONS_CONFIG, 
  ADVANCED_PERMISSIONS 
} from '@/config/permissions/config';
import { getCurrentUser } from '@/utils/auth';

/**
 * Hook principal para gestión de permisos en toda la aplicación
 * Proporciona una API consistente para verificar permisos
 */
export const useAppPermissions = (): BasePermissions => {
  const { permissions: raPermissions, isLoading } = useRaPermissions();
  
  // Obtener información del usuario actual
  const currentUser = useMemo(() => {
    const user = getCurrentUser();
    return {
      id: user?.id || '',
      role: (user?.role || raPermissions || localStorage.getItem('role') || 'User') as UserRole,
      hotelId: user?.hotelId,
      groupId: user?.groupId,
    };
  }, [raPermissions]);

  /**
   * Verifica si el usuario tiene uno de los roles especificados
   */
  const hasRole = (...roles: UserRole[]): boolean => {
    return roles.includes(currentUser.role);
  };

  /**
   * Verifica si el usuario tiene al menos el nivel de rol especificado
   */
  const isAtLeastRole = (minRole: UserRole): boolean => {
    const userLevel = ROLE_LEVELS[currentUser.role];
    const minLevel = ROLE_LEVELS[minRole];
    return userLevel <= minLevel; // Menor número = mayor privilegio
  };

  /**
   * Verifica si el usuario puede realizar una acción en un recurso
   * Incluye verificación de permisos condicionales
   */
  const canPerform = (
    resource: ResourceName, 
    action: ResourceAction,
    resourceData?: any
  ): boolean => {
    // Si está cargando, asumir sin permisos por seguridad
    if (isLoading) return false;

    // Obtener configuración del recurso
    const resourceConfig = PERMISSIONS_CONFIG[resource];
    if (!resourceConfig) {
      console.warn(`Resource "${resource}" not found in permissions config`);
      return false;
    }

    // Verificar permisos básicos
    const basicPermissions = resourceConfig.permissions[action];
    if (basicPermissions?.includes(currentUser.role)) {
      return true;
    }

    // Verificar permisos personalizados si existen
    const customPermissions = resourceConfig.customPermissions?.[action];
    if (customPermissions?.includes(currentUser.role)) {
      return true;
    }

    // Verificar permisos condicionales
    const advancedConfig = ADVANCED_PERMISSIONS[resource];
    if (advancedConfig?.conditionalPermissions?.[action]) {
      const context: PermissionContext = {
        user: currentUser,
        resource: resourceData,
      };

      for (const conditionalPerm of advancedConfig.conditionalPermissions[action]) {
        if (conditionalPerm.roles.includes(currentUser.role)) {
          // Si no hay condición, el rol es suficiente
          if (!conditionalPerm.condition) return true;
          
          // Evaluar la condición
          if (conditionalPerm.condition(context)) return true;
        }
      }
    }

    return false;
  };

  // Crear funciones de conveniencia para acciones comunes
  const permissions: BasePermissions = useMemo(() => ({
    role: currentUser.role,
    
    // Funciones de verificación por acción
    canAccess: (resource: ResourceName) => canPerform(resource, 'access'),
    canList: (resource: ResourceName) => canPerform(resource, 'list'),
    canShow: (resource: ResourceName) => canPerform(resource, 'show'),
    canCreate: (resource: ResourceName) => canPerform(resource, 'create'),
    canEdit: (resource: ResourceName) => canPerform(resource, 'edit'),
    canDelete: (resource: ResourceName) => canPerform(resource, 'delete'),
    canExport: (resource: ResourceName) => canPerform(resource, 'export'),
    canImport: (resource: ResourceName) => canPerform(resource, 'import'),
    
    // Función genérica con el tercer parámetro opcional
    canPerform: (resource: ResourceName, action: ResourceAction, resourceData?: any) => 
      canPerform(resource, action, resourceData),
    
    // Funciones de utilidad
    hasRole,
    isAtLeastRole,
  }), [currentUser, isLoading]);

  return permissions;
};

/**
 * Hook para obtener permisos específicos de un recurso
 * Útil cuando trabajas intensivamente con un solo recurso
 */
export const useResourcePermissions = (
  resource: ResourceName,
  resourceData?: any
) => {
  const basePermissions = useAppPermissions();
  
  return useMemo(() => ({
    // Mantener todas las funciones base
    ...basePermissions,
    
    // Añadir versiones simplificadas para este recurso
    canView: basePermissions.canShow(resource),
    canAdd: basePermissions.canCreate(resource),
    canModify: basePermissions.canEdit(resource),
    canRemove: basePermissions.canDelete(resource),
    
    // Versiones con data para permisos condicionales
    canViewThis: (data?: any) => 
      basePermissions.canPerform(resource, 'show'),
    canEditThis: (data?: any) => 
      basePermissions.canPerform(resource, 'edit'),
    canDeleteThis: (data?: any) => 
      basePermissions.canPerform(resource, 'delete'),
  }), [basePermissions, resource]);
};

/**
 * Hook para verificar múltiples permisos a la vez
 * Útil para mostrar/ocultar múltiples elementos UI
 */
export const useBulkPermissions = (
  checks: Array<{ resource: ResourceName; action: ResourceAction }>
) => {
  const permissions = useAppPermissions();
  
  return useMemo(() => {
    const results: Record<string, boolean> = {};
    
    checks.forEach(({ resource, action }) => {
      const key = `${resource}.${action}`;
      results[key] = permissions.canPerform(resource, action);
    });
    
    return results;
  }, [permissions, checks]);
};

/**
 * Hook para obtener todos los recursos accesibles por el usuario
 * Útil para construir menús dinámicos
 */
export const useAccessibleResources = (): ResourceName[] => {
  const permissions = useAppPermissions();
  
  return useMemo(() => {
    const resources = Object.keys(PERMISSIONS_CONFIG) as ResourceName[];
    return resources.filter(resource => permissions.canAccess(resource));
  }, [permissions]);
};



export default useAppPermissions;